import React, { useEffect, useState } from "react";
import { View, TouchableOpacity, Text, StyleSheet, Image } from "react-native";
import Menu from "../Menu";
import StyleSheetRW from "core/StyleSheetRW";
import { db } from "@react-native-firebase/database";
import { get, ref } from "firebase/database";
import profileIcon from "res/svgs/menu_profile.svg";
import ProgressCircle from "src/components/ProgressCircle";
import Loader from "src/components/Loader";

interface ReviewData {
    question: string;
    answer: string;
    rating: number;
}

interface ReviewSubmissionData {
    result: {
        [sectionTitle: string]: ReviewData[];
    };
    submissionDate: string;
}

interface ProfileData {
    firstName: string;
    lastName: string;
    image: string;
}

interface ReviewAssignmentData {
    key: string;
    ReviewedUserID: string;
    ReviewedUserName: string;
    ReviewerRole: string;
    ReviewerUserID: string;
    ReviewerUserName: string;
    dueDate: string;
    startDate: string;
    status: string;
    year: string;
}

const SubmittedReviewScreen: React.FC = () => {
    const [isCollapsed, setIsCollapsed] = useState<{ [section: string]: boolean }>({});
    const [reviewSubmissionData, setReviewSubmissionData] = useState<ReviewSubmissionData | null>(null);
    const [reviewAssignments, setReviewAssignments] = useState<ReviewAssignmentData[]>([]);
    const [profileId, setProfileId] = useState<string | null>(null);
    const [profileData, setProfileData] = useState<ProfileData | null>(null);
    const [showLoader, setShowLoader] = useState(false);
   
    const params = new URLSearchParams(window.location.search);
    const selectedReviewedUserName = params.get('reviewedUserName');

    const fetchReviewAssignments = async () => {
        setShowLoader(true);
        
        const dbRef = ref(db, "reviewAssignment");
        try {
            const snapshot = await get(dbRef);
            if (snapshot.exists()) {
                const data = snapshot.val();
                // Filter based on ReviewedUserName and include the key in the results
                const filteredData = Object.entries(data).filter(([key, entry]: [string, any]) => {
                    return entry.ReviewedUserName === selectedReviewedUserName && entry.status === "Submitted";
                }).map(([key, entry]: [string, any]) => ({
                    key,
                    ...entry
                }));
                
                setReviewAssignments(filteredData);
                fetchReviewSubmissionData(filteredData);
            } else {
                console.log("No data available");
            }
        } catch (error) {
            console.error("Error fetching review assignments:", error);
        }
    };
    
    const fetchProfileData = async (id: string) => {
        const dbRef = ref(db, `profile/${id}`);
        try {
            const snapshot = await get(dbRef);
            if (snapshot.exists()) {
                return snapshot.val(); // Return profile data
            } else {
                return null;
            }
        } catch (error) {
            console.error("Error fetching profile data:", error);
            return null;
        }
    };

    const fetchReviewSubmissionData = async (assignments: ReviewAssignmentData[]) => {
        const promises = assignments.map(async (assignment) => {
            const dbRef = ref(db, `reviewSubmission/${assignment.key}`);
            try {
                const snapshot = await get(dbRef);
                if (snapshot.exists()) {
                    const submissionData = snapshot.val();
                    // Fetch profile data for the reviewer
                    const profileData = await fetchProfileData(assignment.ReviewerUserID);
                    return {
                        key: assignment.key,
                        data: {
                            ...submissionData,
                            profileData: profileData // Add profile data to submissionData
                        }
                    };
                } else {
                    return {
                        key: assignment.key,
                        data: null
                    };
                }
            } catch (error) {
                console.error("Error fetching review submission data:", error);
                return {
                    key: assignment.key,
                    data: null
                };
            }
        });
   
        const results = await Promise.all(promises);
       
        const combinedData: ReviewSubmissionData = {
            result: {},
            submissionDate: "" 
        };

        results.forEach(result => {
            if (result.data) {
                Object.keys(result.data.result).forEach(section => {
                    if (!combinedData.result[section]) {
                        combinedData.result[section] = [];
                    }
                    combinedData.result[section].push({
                        ...result.data.result[section],
                        profileData: result.data.profileData // Include profile data in each section
                    });
                });
            }
        });

        setReviewSubmissionData(combinedData);
        setShowLoader(false);
    };

    useEffect(() => {
        fetchReviewAssignments();
    }, []);

    const toggleCollapse = (section: string) => {
        setIsCollapsed(prevState => ({
            ...prevState,
            [section]: !prevState[section]
        }));
    };
    
    return (
        <View style={styles.mainContainer}>
            <Menu />
            {showLoader && <Loader />}
            <View style={styles.container}>
                <Text style={styles.nameText}>{selectedReviewedUserName} Review Summary</Text>
                {reviewSubmissionData && Object.keys(reviewSubmissionData.result).map(section => (
                    <Section
                        key={section}
                        title={section}
                        data={reviewSubmissionData.result[section]}
                        isCollapsed={isCollapsed[section]}
                        toggleCollapse={() => toggleCollapse(section)}
                        profileData={profileData}
                    />
                ))}
            </View>
        </View>
    );
};

interface SectionProps {
    title: string;
    data: ReviewData[];
    isCollapsed: boolean;
    toggleCollapse: () => void;
    profileData: ProfileData | null;
}

const Section: React.FC<SectionProps> = ({ title, data, isCollapsed, toggleCollapse, profileData }) => {
    let questionAnswersMap = {};

    data.forEach(item => {
        Object.keys(item).forEach(key => {
            if (key !== 'profileData') {
                const reviewItem = item[key];
                if (!questionAnswersMap[reviewItem.question]) {
                    questionAnswersMap[reviewItem.question] = [];
                }
                questionAnswersMap[reviewItem.question].push({
                    answer: reviewItem.answer,
                    rating: reviewItem.rating,
                    profileData: item.profileData
                });
            }
        });
    });

    return (
        <View style={styles.inputContainer}>
            <TouchableOpacity onPress={toggleCollapse}>
                <Text style={styles.label}>{isCollapsed ? '+' : '-'} {title}</Text>
            </TouchableOpacity>
            <View style={styles.separator2} />
            {!isCollapsed && (
                <View style={styles.questionsContainer}>
                    <View style={styles.verticalLineContainer}>
                        <View style={styles.verticalLine} />
                        {Object.keys(questionAnswersMap).map((question, index) => (
                            <React.Fragment key={index}>
                                <View style={styles.questionRow}>
                                    <Text style={styles.circleNumber}>{index + 1}</Text>
                                    <Text style={styles.subHeading}>{question}</Text>
                                </View>
                                {questionAnswersMap[question].map((answerItem, subIndex) => (
                                    <View style={styles.row} key={subIndex}>
                                        <View style={styles.column}>
                                            <View style={styles.imgContainer}>
                                                {answerItem.profileData && answerItem.profileData.image ? (
                                                    <Image source={{ uri: answerItem.profileData.image }} style={styles.img} />
                                                ) : (
                                                    <Image source={profileIcon} style={styles.img} />
                                                )}
                                            </View>
                                            <Text style={styles.profileName}>
                                                {answerItem.profileData ? `${answerItem.profileData.firstName} ${answerItem.profileData.lastName}` : 'name'}
                                            </Text>
                                        </View>
                                        <View style={styles.progressWrapper}>
                                            <ProgressCircle score={answerItem.answer === "N/A" ? 0 : (answerItem.rating / 5) * 100} />
                                        </View>
                                        <View style={[styles.column, { width: "250px", marginLeft: 80 }]}>
                                            <Text style={styles.commentsHeading}>Comments</Text>
                                            <Text style={styles.comments}>{answerItem.answer}</Text>
                                        </View>
                                    </View>
                                ))}
                                <View style={styles.separator} />
                            </React.Fragment>
                        ))}
                    </View>
                </View>
            )}
        </View>
    );
};


const styles = StyleSheetRW.create(() => ({
    separator: {
        borderBottomWidth: 1,
        borderBottomColor: '#2F485850',
        marginHorizontal:35
       
    },
    separator2: {
        borderBottomWidth: 1,
        borderBottomColor: '#2F4858',
        marginHorizontal:15
       
    },
    verticalLineContainer: {
        flex: 1,
        position: 'relative',
    },
    verticalLine: {
        position: 'absolute',
        left: 16, 
        top: 10,
        bottom: 10, 
        width: 1,
        backgroundColor: '#000000',
        zIndex:-1
    },
    nameText:{fontSize:28,color:"#2F4858",fontWeight:"700",marginBottom:10},
    container: { flex: 1, padding: 20, overflow: "scroll" },
    mainContainer: { display: "flex", flexDirection: "row", height: "100%" },
    inputContainer: { marginVertical: 10 },
    label: { fontSize: 22, fontWeight: '700', marginBottom: 5 },
    subHeading: { fontSize: 18, fontWeight: '700', marginBottom: 5,  marginTop: 5,marginLeft:5 },
    row: { display: "flex", flexDirection: "row", paddingVertical: 10, paddingHorizontal: 40 },
    column: { flexDirection: "column",width:"20%" },
    img: { width: 60, height: 60, borderRadius:100 },
    imgContainer: { width: 60, height: 60, },
    progressContainer: { justifyContent: 'center', alignItems: 'center', position: 'relative', width: 60, height: 60 },
    progressWrapper: { width: 60, height: 60, justifyContent: 'center', alignItems: 'center', marginLeft: 40 },
    progressText: { position: 'absolute', fontSize: 16, fontWeight: 'bold', textAlign: 'center', width: '100%', lineHeight: 60 },
    commentsHeading: { fontSize: 16, fontWeight: '600', marginTop: 5 },
    comments: { fontSize: 12, fontWeight: '400' },
    circleNumber:{fontSize:10,fontWeight:"700",borderRadius:12,borderWidth:2,width:17,height:17,marginLeft: 8,textAlign:"center",paddingBottom:2,marginTop:0,zIndex:100,backgroundColor:"#ffffff" },
    profileName:{ fontSize: 12, fontWeight: '500',  },
    questionsContainer: {
        flexDirection: 'row',
        position: 'relative',
    },
    questionRow: {
        flexDirection: 'row',
        alignItems: 'center',
    },
}));

export default SubmittedReviewScreen;
